import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import moment from 'moment';
import { NgxSpinnerService } from 'ngx-spinner';
import { Toaster } from 'ngx-toast-notifications';
import { merge, of, Subject } from 'rxjs';
import { catchError, filter, finalize, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { CurrencySymbolPipe } from 'src/app/shared/pipes/currency-symbol.pipe';
import { NumberSpacingPipe } from 'src/app/shared/pipes/number-spacing.pipe';
import { getTodayDateOnly } from 'src/app/shared/util/date.util';
import { showToast } from 'src/app/shared/util/toaster.util';
import { GreaterThanZeroValidator } from 'src/app/shared/validators/greater-than-zero.validators';
import { RequiredValidator } from 'src/app/shared/validators/required.validator';
import { TEAValidation } from 'src/app/shared/validators/tea-value.validators';
import { OtdInvoiceRow, OtdSaleDetail, OtdSaleOperation, OtdService } from '../../../services/otd.service';

export interface OtdSaleModalData {
  invoices: OtdInvoiceRow[];
  invoiceIds: string[];
}

const FUND = 'Genera';
const DEFAULT_FUND_TEA = '12';
const MAXIMUM_FUND_TEA = 20;
const MAXIMUM_TRANSACTION_ID_LENGTH = 7;
const MAXIMUM_VOUCHER_SIZE_IN_BYTES = 5 * 1024 * 1024;
const VOUCHER_TYPES = /^data:(image\/jpeg|image\/png|application\/pdf)[;,]/;
const MINIMUM_REMAINING_DAYS = 15;
const SALE_REGISTERED = 'Venta registrada';

export function voucherSizeValidator(control: AbstractControl): ValidationErrors | null {
  const voucher: string = control.value;
  if (!voucher) {
    return null;
  }
  const base64 = voucher.substring(voucher.indexOf(',') + 1);
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  const sizeInBytes = (base64.length * 3) / 4 - padding;
  return sizeInBytes > MAXIMUM_VOUCHER_SIZE_IN_BYTES ? { voucherTooLarge: true } : null;
}

export function voucherTypeValidator(control: AbstractControl): ValidationErrors | null {
  const voucher: string = control.value;
  return !voucher || VOUCHER_TYPES.test(voucher) ? null : { voucherType: true };
}

export function finalizeDateNotAfterSaleDate(form: AbstractControl): ValidationErrors | null {
  const finalizeDate = form.get('finalizeDate').value;
  const saleDate = form.get('saleDate').value;
  return finalizeDate && saleDate && finalizeDate > saleDate ? { finalizeDateAfterSaleDate: true } : null;
}

// On the form, not the TEA control: a control error would make the shared input swap its label for its own "18%" text.
export function fundTeaNotAboveMaximum(form: AbstractControl): ValidationErrors | null {
  return Number(form.get('fundTea').value) > MAXIMUM_FUND_TEA ? { fundTeaAboveMaximum: true } : null;
}

@Component({
  selector: 'app-otd-sale-modal',
  templateUrl: './otd-sale-modal.component.html',
  styleUrls: ['./otd-sale-modal.component.scss'],
})
export class OtdSaleModalComponent implements OnInit, OnDestroy {
  funds = [{ value: FUND, text: FUND }];
  maximumFundTea = MAXIMUM_FUND_TEA;
  form: FormGroup;
  detail: OtdSaleDetail | null = null;
  saleFailed = false;

  private earliestDate: string | null = null;

  private currencySymbol = new CurrencySymbolPipe();
  private numberSpacing = new NumberSpacingPipe();
  private destroyed = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: OtdSaleModalData,
    private dialogRef: MatDialogRef<OtdSaleModalComponent>,
    private otdService: OtdService,
    private formBuilder: FormBuilder,
    private spinner: NgxSpinnerService,
    private toaster: Toaster
  ) {
    const today = getTodayDateOnly();
    this.form = this.formBuilder.group(
      {
        fund: [FUND, Validators.required],
        fundTea: [DEFAULT_FUND_TEA, [RequiredValidator, GreaterThanZeroValidator]],
        finalizeDate: [today, Validators.required],
        saleDate: [today, Validators.required],
        transactionIdInput: ['', Validators.maxLength(MAXIMUM_TRANSACTION_ID_LENGTH)],
        transactionIds: this.formBuilder.array([], Validators.required),
        voucher: [null, [voucherTypeValidator, voucherSizeValidator]],
      },
      { validators: [finalizeDateNotAfterSaleDate, fundTeaNotAboveMaximum] }
    );
  }

  ngOnInit() {
    const fundTea = this.form.get('fundTea');
    const saleDate = this.form.get('saleDate');
    const finalizeDate = this.form.get('finalizeDate');

    merge(fundTea.valueChanges, saleDate.valueChanges, finalizeDate.valueChanges)
      .pipe(
        startWith(null),
        filter(
          () =>
            fundTea.valid &&
            !fundTeaNotAboveMaximum(this.form) &&
            saleDate.valid &&
            finalizeDate.valid &&
            !finalizeDateNotAfterSaleDate(this.form)
        ),
        switchMap(() =>
          this.otdService
            .getSaleDetail(
              this.data.invoiceIds,
              saleDate.value,
              String(fundTea.value),
              finalizeDate.value
            )
            .pipe(catchError(() => of(null)))
        ),
        takeUntil(this.destroyed)
      )
      .subscribe((detail) => {
        this.detail = detail;
        if (detail) {
          this.earliestDate = this.earliestDateOf(detail);
        }
      });
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }

  get title(): string {
    const count = this.data.invoices.length;
    return `Vender ${count} ${count === 1 ? 'factura' : 'facturas'} a un fondo`;
  }

  get transactionIds(): FormArray {
    return this.form.get('transactionIds') as FormArray;
  }

  get isTeaAboveMaximum(): boolean {
    return this.form.hasError('fundTeaAboveMaximum');
  }

  get isTransactionIdTooLong(): boolean {
    return this.form.get('transactionIdInput').hasError('maxlength');
  }

  get isFinalizeDateAfterSaleDate(): boolean {
    return this.form.hasError('finalizeDateAfterSaleDate');
  }

  get canConfirm(): boolean {
    return this.form.valid && !!this.detail;
  }

  get closingNotice(): string | null {
    if (!this.detail || !this.canConfirm) {
      return null;
    }
    const investments = this.detail.totals.investmentsToClose;
    const leaving = investments === 1 ? 'sale 1 inversión' : `salen las ${investments} inversiones`;
    const invoices = this.data.invoices.length === 1 ? 'la factura queda' : 'las facturas quedan';
    const finalizeDate = moment(this.form.get('finalizeDate').value).format('DD/MM/YYYY');
    return (
      `Al confirmar, ${leaving} con su capital y su retorno al ${finalizeDate}, ` +
      `y ${invoices} al 100% con ${FUND}. ` +
      'Avisamos a Operaciones en #coordinación-genera para la transferencia en Cavali.'
    );
  }

  addTransactionId() {
    const transactionIdInput = this.form.get('transactionIdInput');
    if (!transactionIdInput.value || transactionIdInput.invalid) {
      return;
    }
    this.transactionIds.push(this.formBuilder.control(transactionIdInput.value));
    transactionIdInput.setValue('');
  }

  removeTransactionId(index: number) {
    this.transactionIds.removeAt(index);
  }

  isSaleDateSelectable = (date: moment.Moment | Date | null): boolean => {
    if (!date) {
      return true;
    }
    const day = this.dateOnly(date);
    return this.isOnOrAfterEarliestDate(day) && day <= this.latestSaleDate;
  };

  isFinalizeDateSelectable = (date: moment.Moment | Date | null): boolean => {
    if (!date) {
      return true;
    }
    const day = this.dateOnly(date);
    return this.isOnOrAfterEarliestDate(day) && day <= this.form.get('saleDate').value;
  };

  operationCode(operation: OtdSaleOperation): string {
    const selectedRow = this.data.invoices.find((invoice) => invoice.id === operation.id);
    return (selectedRow ? selectedRow.codes : operation.codes).join(', ');
  }

  formatAmount(amount: string): string {
    const currency = this.data.invoices[0]?.currency;
    return `${this.currencySymbol.transform(currency)} ${this.numberSpacing.transform(amount)}`;
  }

  confirmSale() {
    if (!this.canConfirm) {
      return;
    }
    const { fundTea, saleDate, finalizeDate, voucher } = this.form.value;
    this.spinner.show();
    this.otdService
      .confirmSale({
        invoiceIds: this.data.invoiceIds,
        saleDate,
        finalizeDate,
        fundTea: Number(fundTea),
        transactionIds: this.transactionIds.value,
        ...(voucher ? { voucher } : {}),
      })
      .pipe(
        finalize(() => this.spinner.hide()),
        takeUntil(this.destroyed)
      )
      .subscribe({
        next: () => {
          showToast(this.toaster, SALE_REGISTERED, true, true);
          this.dialogRef.close(true);
        },
        error: () => {
          this.saleFailed = true;
        },
      });
  }

  returnToSale() {
    this.saleFailed = false;
  }

  close() {
    this.dialogRef.close();
  }

  private isOnOrAfterEarliestDate(day: string): boolean {
    const earliestDate = this.earliestDate;
    return !earliestDate || day >= earliestDate;
  }

  private dateOnly(date: moment.Moment | Date): string {
    return moment(date).format('YYYY-MM-DD');
  }

  private get latestSaleDate(): string {
    const earliestPaymentDate = this.data.invoices
      .map((invoice) => invoice.paymentDate)
      .sort()
      .shift();
    return moment(earliestPaymentDate).subtract(MINIMUM_REMAINING_DAYS, 'days').format('YYYY-MM-DD');
  }

  private earliestDateOf(detail: OtdSaleDetail): string | null {
    const operationDates = detail.operations
      .reduce((dates, operation) => [...dates, operation.advancePaymentDate, operation.latestInvestmentDate], [])
      .filter(Boolean);
    return operationDates.length ? operationDates.sort().pop() : null;
  }
}
